import React, { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle, Sparkles, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import StreakDisplay from './StreakDisplay';
import DropsDialog from '../drops/DropsDialog';

interface BulletinsHeaderProps {
  activeTab: string;
  activeCategory?: string;
  queuedCount?: number;
}

const BulletinsHeader = ({
  activeTab,
  activeCategory,
  queuedCount = 0
}: BulletinsHeaderProps) => {
  const navigate = useNavigate();
  const [dropsDialogOpen, setDropsDialogOpen] = useState(false);

  return (
    <div className="mb-4 sm:mb-6 h-auto sm:h-[240px] mx-0">
      <div className="flex flex-col gap-2 mx-0">
        {/* Title only */}
        <div className="h-auto sm:h-[100px] mx-0 my-0 mb-2 sm:mb-0">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-fade-in">
            {activeTab === 'updates' ? 'updates' : activeTab === 'top-follows' ? 'never miss a beat' : 'pour toi'}
          </h1>
        </div>

        <div className="h-auto sm:h-[60px] mb-2 sm:mb-0">
          {activeTab === 'pour-toi' && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold bg-gradient-to-r from-gray-200 to-gray-400 bg-clip-text text-transparent">
                  Artstream
                </h2>
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="cursor-pointer">
                        <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 hover:text-gray-300 transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[400px] p-4 space-y-4">
                      <p>This isn't your highlight reel.</p>
                      <p>Every post appears simultaneously to all users in chronological order - no popularity contests, no hidden rankings. What you see is what's happening right now, from midnight poetry to lunchbreak rants, unfiltered and uncurated.</p>
                      
                      <div className="space-y-2">
                        <p className="font-semibold">Key Features:</p>
                        <p>✅ 24-Hour Time Capsules</p>
                        <p className="text-sm text-gray-400">Posts fade like sunset, making space for fresh perspectives daily</p>
                        
                        <p>✅ Equal-Playground Visibility</p>
                        <p className="text-sm text-gray-400">Your 3 AM thoughts get the same real estate as CEO morning posts</p>
                        
                        <p>✅ Randomized Layout</p>
                        <p className="text-sm text-gray-400">Posts have a random chance to be different aspect ratios. There's no algorithm for this, it's just randomized.</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              {/* Icon buttons positioned underneath Bulletins text */}
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setDropsDialogOpen(true)}
                        className="relative w-8 h-8 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/40 text-purple-300 hover:text-purple-200 hover:border-purple-400/60 hover:bg-gradient-to-r hover:from-purple-600/30 hover:to-pink-600/30 transition-all duration-300"
                      >
                        <Sparkles className="w-4 h-4" />
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Drops</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => navigate('/roadmap')} 
                        className="w-8 h-8 text-primary hover:text-primary/90 border border-input hover:bg-accent"
                      >
                        <Map className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Roadmap</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
          
          {activeTab === 'top-follows' && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold bg-gradient-to-r from-gray-200 to-gray-400 bg-clip-text text-transparent">
                  Follow Feed
                </h2>
              </div>
            </div>
          )}
        </div>

        <div className="h-auto sm:h-[39px] flex items-center justify-between">
          <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-2xl animate-fade-in opacity-80">
            {activeTab === 'updates' ? "Official updates and announcements from the team." : activeTab === 'top-follows' ? "Your favorite creators and their latest posts from the last 24 hours." : activeCategory === 'all' ? "All permanent posts from the community, including mature content and various categories." : "Latest posts from the community. All posts are now permanent with mature content filtering and tags."}
          </p>
          
          <StreakDisplay queuedCount={queuedCount} />
        </div>
      </div>
      
      <DropsDialog 
        open={dropsDialogOpen} 
        onOpenChange={setDropsDialogOpen} 
      />
    </div>
  );
};

export default BulletinsHeader;
